// Que un post con cambios sin guardar no se pierda al irse — ADR-2026-10-07-el-post-se-guarda-con-un-boton.
//
// La app usa `BrowserRouter`, que no trae el bloqueador de React Router (`useBlocker` es de
// los data routers). Así que se cubren a mano las tres salidas que existen:
//   · cerrar o recargar la pestaña → `beforeunload` (el navegador muestra su propio cartel);
//   · un link de la app (la sidebar, un `<a>` interno) → se frena el click en captura y se
//     pregunta; si elige, se navega al mismo destino;
//   · el botón atrás del navegador → se deja una entrada de historial "de guardia" con la
//     MISMA URL: el atrás la consume, React Router no ve cambio de ruta y se pregunta.
// Las salidas de la propia pantalla (el volver, abrir otro post) no pasan por acá: preguntan
// directo con el mismo modal.

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/** Adónde iba cuando se lo frenó: una ruta de la app, o "atrás". */
export type DestinoFrenado = { tipo: 'ruta'; a: string } | { tipo: 'atras' };

/**
 * `preguntar`: hay algo que se perdería (activa el cartel del navegador al cerrar la pestaña).
 * `interceptar`: frenar los links y el atrás para decidir qué hacer antes de irse — incluye
 * al post nuevo y vacío, que no pregunta nada pero se borra al salir.
 */
export function useGuardiaDeSalida(
  { preguntar, interceptar }: { preguntar: boolean; interceptar: boolean },
  onFrenado: (destino: DestinoFrenado) => void,
) {
  const navigate = useNavigate();
  const avisarRef = useRef(onFrenado);
  avisarRef.current = onFrenado;
  /** La entrada de guardia está puesta y nadie la consumió. */
  const guardiaPuesta = useRef(false);
  const ponerGuardia = () => {
    window.history.pushState(window.history.state, '', window.location.href);
    guardiaPuesta.current = true;
  };

  // Cerrar o recargar la pestaña.
  useEffect(() => {
    if (!preguntar) return;
    const antes = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', antes);
    return () => window.removeEventListener('beforeunload', antes);
  }, [preguntar]);

  // Un link interno. En captura, para llegar antes que el `Link` de React Router.
  useEffect(() => {
    if (!interceptar) return;
    const alClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const destino = url.pathname + url.search + url.hash;
      if (destino === window.location.pathname + window.location.search + window.location.hash) return;
      e.preventDefault();
      e.stopPropagation();
      avisarRef.current({ tipo: 'ruta', a: destino });
    };
    document.addEventListener('click', alClick, true);
    return () => document.removeEventListener('click', alClick, true);
  }, [interceptar]);

  // El botón atrás: una entrada de guardia con la misma URL y el mismo `state` (el de React
  // Router lleva su índice: copiarlo hace que no registre un cambio al consumirla).
  useEffect(() => {
    if (!interceptar) return;
    // Ya hay una puesta (pasó de "post nuevo vacío" a "con cambios"): no se apila otra.
    if (!guardiaPuesta.current) ponerGuardia();
    const alVolver = () => {
      if (!guardiaPuesta.current) return;
      guardiaPuesta.current = false;
      avisarRef.current({ tipo: 'atras' });
    };
    window.addEventListener('popstate', alVolver);
    // Al desmontar NO se hace `history.back()`: si se desmontó porque se navegó a otra ruta,
    // ese atrás lo devolvería acá, y en desarrollo el doble efecto de StrictMode lo cruzaba
    // con el `pushState` siguiente. La guardia sobrante se saca con `retirar` cuando la
    // pantalla cierra el post por sus propios botones.
    return () => window.removeEventListener('popstate', alVolver);
  }, [interceptar]);

  return {
    /** Seguir hacia donde iba, después de guardar o descartar. */
    continuar: (destino: DestinoFrenado) => {
      if (destino.tipo === 'atras') return window.history.back();
      // La entrada de guardia se reemplaza por la ruta nueva: así no queda un "atrás" que
      // no hace nada en el medio.
      const reemplazar = guardiaPuesta.current;
      guardiaPuesta.current = false;
      navigate(destino.a, { replace: reemplazar });
    },
    /** Cerró el post desde la pantalla (guardar, descartar, volver): la guardia sobra. */
    retirar: () => {
      if (!guardiaPuesta.current) return;
      guardiaPuesta.current = false;
      window.history.back();
    },
    /** Eligió quedarse: si lo frenó el atrás, la guardia se gastó y se vuelve a poner. */
    quedarse: (destino: DestinoFrenado) => {
      if (destino.tipo === 'atras' && interceptar) ponerGuardia();
    },
  };
}
